import styled, { css } from 'styled-components';
import { deviceLess } from '_constants/DEVICES';
import { TOP_BAR_HEIGHT } from '../TopBar/styles';

export const RAIL_WIDTH = 84;
export const RAIL_COLLAPSED_WIDTH = 56;

const TEAL = '#2B4E56';
const TEXT = 'rgb(255, 237, 220)';
const CORAL = '#F5A687';

export const RailContainer = styled.nav`
  position: fixed;
  top: ${TOP_BAR_HEIGHT}px;
  left: 0;
  bottom: 0;
  width: ${({ $collapsed }) => ($collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH)}px;
  /* Nav chrome sits above page content. The rail and the top bar used to be 60/70, which put them
     under the docked map sidebar (modules/Modals/old/mapPopup, z-index 140) — and because the top
     bar is a stacking context, its own dropdowns (notifications, account) were dragged under with
     it however high their inner z-index went. Kept below the command palette (1300) and the modal
     layer (ui-kit/Modal 9000, Drawer 9001), which are meant to cover the navigation. */
  z-index: 1000;
  display: none;
  flex-direction: column;
  background: ${TEAL};
  color: ${TEXT};
  transition: width 120ms ease;

  @media ${deviceLess.tableView} {
    display: flex;
  }

  /* When a docked side drawer is open it raises this class on <body> so the rail (and its
     hover label tooltips, which pop out over the content) sit above the drawer overlay.
     Scoped to this state so the rail still sits below full-screen centered modals. */
  body.nav-rail-elevated & {
    z-index: 9100;
  }
`;

export const RailItems = styled.ul`
  flex: 1;
  margin: 0;
  padding: 8px 0 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: visible;
`;

export const RailItem = styled.li`
  position: relative;
  display: flex;
`;

export const ActiveAccent = styled.span`
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: ${CORAL};
`;

export const Tooltip = styled.span`
  position: absolute;
  left: 100%;
  top: 50%;
  margin-left: 8px;
  transform: translateY(-50%);
  padding: 4px 8px;
  background: #1f3a40;
  color: ${TEXT};
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 120ms ease;
  text-transform: capitalize;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  z-index: 70;
`;

/* Rendered as a <Link> when the section navigates straight to a page, and as a <button> when it only
   opens the sub-pages popup (nothing to link to) — see Rail/index.js. text-decoration is reset for the
   anchor case. */
export const RailLink = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 6px;
  background: transparent;
  border: 0;
  color: ${TEXT};
  font: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: background 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.14);
    box-shadow: inset 0 0 0 2px ${CORAL};
  }

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(255, 255, 255, 0.08);
    `}

  ${({ $showTooltip }) =>
    $showTooltip &&
    css`
      &:hover ${Tooltip}, &:focus-visible ${Tooltip} {
        opacity: 1;
      }
    `}
`;

export const IconWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const SubpagesArrow = styled.span`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${CORAL};
  opacity: 0.85;
  pointer-events: none;
`;

export const RailLabel = styled.span`
  font-size: 10px;
  line-height: 1.2;
  text-transform: capitalize;
  letter-spacing: 0.02em;
  text-align: center;
  margin-top: 4px;
`;

export const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  background: transparent;
  border: 0;
  border-top: 1px solid rgba(255, 237, 220, 0.08);
  color: ${TEXT};
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  transition: background 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.14);
    box-shadow: inset 0 0 0 2px ${CORAL};
  }
`;
